import {
    Box,
    Button,
    Center,
    Checkbox,
    Flex,
    FormControl,
    FormLabel,
    Input,
    Select,
    Stack,
    Text,
} from '@chakra-ui/react';
import { AddIcon, DeleteIcon } from '@chakra-ui/icons';
import { useEffect, useState } from 'react';
import usePipelineStore from '../../../../stores/pipeline-store';
import { fetchHandlers, Handler } from '../../../../api/handlers-api';
import { VarSelect } from '../inputs';

type ExternalRequestStepSettingsProps = {
    stepId: string;
};

export function ExternalRequestStepSettings({ stepId }: ExternalRequestStepSettingsProps) {
    const { pipeline, updateStep } = usePipelineStore((state) => state);
    const step = pipeline.steps[stepId] as ExternalRequestStep;
    const [handlers, setHandlers] = useState<Handler[]>([]);

    const scriptOptions = ['script1', 'script2', 'script3', 'script4'] as const;
    const script = step.script ?? '';
    const handler = step.handler ?? '';

    useEffect(() => {
        if (step.requestType === 'run_handler' && script) {
            fetchHandlers(script).then(setHandlers);
        } else {
            setHandlers([]);
        }
    }, [step.requestType, script]);

    return (
        <Box>
            <FormControl>
                <FormLabel>Request Type</FormLabel>
                <Select
                    color={'black'}
                    value={step.requestType}
                    backgroundColor="white"
                    onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
                        updateStep(stepId, 'requestType', value);
                    }}
                >
                    <option value="cycle_update" disabled={false}>
                        Cycle Update
                    </option>
                    <option value="get" disabled={false}>
                        HTTP GET
                    </option>
                    <option value="update_cycle_state" disabled={false}>
                        Update Cycle State
                    </option>
                    <option value="update_dashboard_instance_state" disabled={false}>
                        Update Dashboard Instance State
                    </option>
                    <option value="run_handler" disabled={false}>
                        Run Handler
                    </option>
                </Select>
            </FormControl>
            {step.requestType === 'run_handler' && (
                <>
                    <FormControl mt={2}>
                        <FormLabel>Script</FormLabel>
                        <Select
                            color={script ? 'black' : 'grey'}
                            value={script ?? ''}
                            backgroundColor="white"
                            onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
                                updateStep(stepId, 'script', value);
                                updateStep(stepId, 'handler', '');
                            }}
                        >
                            <option value="" disabled>
                                Select script
                            </option>
                            {scriptOptions.map((s) => (
                                <option key={s} value={s}>
                                    {s}
                                </option>
                            ))}
                        </Select>
                    </FormControl>
                    {script && (
                        <FormControl mt={2}>
                            <FormLabel>Handler</FormLabel>
                            <Select
                                color={handler ? 'black' : 'grey'}
                                value={handler ?? ''}
                                backgroundColor="white"
                                onChange={({ target: { value } }: React.ChangeEvent<HTMLSelectElement>) => {
                                    updateStep(stepId, 'handler', value);
                                }}
                            >
                                <option value="" disabled>
                                    Select handler
                                </option>
                                {handlers.map((h) => (
                                    <option key={h.id} value={h.name}>
                                        {h.name}
                                    </option>
                                ))}
                            </Select>
                        </FormControl>
                    )}
                    <FormControl mt={2}>
                        <Checkbox
                            isChecked={step.runHandlerAsync ?? false}
                            onChange={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
                                updateStep(stepId, 'runHandlerAsync', checked);
                            }}
                        >
                            <Text fontSize="sm">Execute async (do not wait for handler to complete)</Text>
                        </Checkbox>
                    </FormControl>
                </>
            )}
            {step.requestType === 'get' && (
                <FormControl mt={2}>
                    <FormLabel>Url</FormLabel>
                    <Input
                        backgroundColor="white"
                        placeholder="https://..."
                        value={step.requestUrl}
                        onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                            updateStep(stepId, 'requestUrl', value);
                        }}
                    />
                </FormControl>
            )}

            <FormControl mt={2}>
                <FormLabel>Payload</FormLabel>
                <Stack>
                    {step.payload.map((paramElem, index) => {
                        return (
                            <Flex key={index} borderWidth="1px" borderRadius="md" padding={2}>
                                <Stack>
                                    <Flex>
                                        <Center>
                                            <Button
                                                backgroundColor="white"
                                                _hover={{ color: '#f0706e' }}
                                                _focus={{ backgroundColor: 'white' }}
                                                _active={{ backgroundColor: 'white' }}
                                                color={'grey'}
                                                leftIcon={<DeleteIcon />}
                                                onClick={() => {
                                                    const payload = JSON.parse(JSON.stringify(step.payload));
                                                    payload.splice(index, 1);
                                                    updateStep(stepId, 'payload', payload);
                                                }}
                                            />
                                        </Center>
                                        <Center>
                                            <Text mr={2}>Key:</Text>
                                        </Center>
                                        <Input
                                            backgroundColor="white"
                                            placeholder="key"
                                            value={paramElem.key}
                                            onChange={({ target: { value } }: React.ChangeEvent<HTMLInputElement>) => {
                                                const payload = JSON.parse(JSON.stringify(step.payload));
                                                payload[index].key = value;
                                                updateStep(stepId, 'payload', payload);
                                            }}
                                        />
                                    </Flex>
                                    <Checkbox
                                        minW={100}
                                        pl={2}
                                        isChecked={paramElem.valueIsVar}
                                        onChange={({ target: { checked } }: React.ChangeEvent<HTMLInputElement>) => {
                                            const payload = JSON.parse(JSON.stringify(step.payload));
                                            payload[index].valueIsVar = checked;
                                            updateStep(stepId, 'payload', payload);
                                        }}
                                    >
                                        <Text fontSize={'xs'}>Use var for value</Text>
                                    </Checkbox>
                                    <Flex>
                                        <Center>
                                            <Text mr={2}>Value:</Text>
                                        </Center>

                                        {paramElem.valueIsVar ? (
                                            <VarSelect
                                                value={paramElem.valueVarId ?? ''}
                                                placeholder="Var"
                                                onChange={(value) => {
                                                    const payload = JSON.parse(JSON.stringify(step.payload));
                                                    payload[index].valueVarId = value;
                                                    updateStep(stepId, 'payload', payload);
                                                }}
                                            />
                                        ) : (
                                            <Input
                                                ml={2}
                                                backgroundColor="white"
                                                placeholder=""
                                                type={'text'}
                                                value={paramElem.value}
                                                onChange={({
                                                    target: { value },
                                                }: React.ChangeEvent<HTMLInputElement>) => {
                                                    const payload = JSON.parse(JSON.stringify(step.payload));
                                                    payload[index].value = value;
                                                    updateStep(stepId, 'payload', payload);
                                                }}
                                            />
                                        )}
                                    </Flex>
                                </Stack>
                            </Flex>
                        );
                    })}

                    <Button
                        backgroundColor="white"
                        _hover={{ backgroundColor: 'white', color: 'black' }}
                        _focus={{ backgroundColor: 'white' }}
                        _active={{ backgroundColor: 'white' }}
                        color={'grey'}
                        leftIcon={<AddIcon />}
                        onClick={() => {
                            const payload = JSON.parse(JSON.stringify(step.payload));
                            payload.push({
                                key: '',
                                valueIsVar: true,
                                value: '',
                                valueVarId: '',
                            });
                            updateStep(stepId, 'payload', payload);
                        }}
                    >
                        Add param
                    </Button>
                </Stack>
            </FormControl>

            <FormControl mt={2}>
                <FormLabel>Response target var</FormLabel>
                <VarSelect
                    value={step.responseTargetVarId}
                    emptyOptionLabel="Ignore response"
                    filterByType={['STRING']}
                    onChange={(value) => updateStep(stepId, 'responseTargetVarId', value)}
                />
            </FormControl>
        </Box>
    );
}
